import { DEFAULT_BRAND_ID, brands, getActiveBrandId } from './index';
import type { BrandId } from './types';

/**
 * One source of truth for technical SEO eligibility. This deliberately does
 * not decide whether a plan can be sold: checkout/catalogue eligibility stays
 * in lib/planFilters.ts.
 */
export type SeoRouteFamily = 'product' | 'editorial' | 'destination' | 'airport-guide' | 'travel-guide' | 'utility' | 'transactional';

export type SeoRouteDecision = {
  shouldBuildRoute: boolean;
  shouldIndexRoute: boolean;
  reason: string;
};

function isPreviewBrand(id: BrandId): boolean {
  const brand = brands[id];
  if (!brand) return true;
  if (id === DEFAULT_BRAND_ID) return false;
  return brand.tech?.robotsPolicy === 'noindex-preview' || brand.tech?.domainStatus !== 'owned';
}

/** Resolve build/index policy without changing commercial plan eligibility. */
export function resolveSeoRoutePolicy(
  family: SeoRouteFamily,
  id: BrandId = getActiveBrandId(),
): SeoRouteDecision {
  const brand = brands[id];
  if (!brand) {
    return { shouldBuildRoute: false, shouldIndexRoute: false, reason: 'unknown brand is not eligible for publication' };
  }

  if (family === 'transactional') {
    return { shouldBuildRoute: true, shouldIndexRoute: false, reason: 'transactional routes are never indexed' };
  }

  // These are bespoke portfolio experiments, not shared eSIMSupra content.
  // Keep their static previews reachable, but do not let a different brand
  // index another brand's editorial surface.
  if (family === 'airport-guide' && id !== 'airport-fast-lane') {
    return { shouldBuildRoute: true, shouldIndexRoute: false, reason: 'airport guides belong to the SkyLane brand only' };
  }
  if (family === 'travel-guide' && id !== 'travel-destination-blog-affiliate-engine') {
    return { shouldBuildRoute: true, shouldIndexRoute: false, reason: 'travel guides belong to the travel-media brand only' };
  }

  if (family === 'product' && brand.siteType !== 'storefront') {
    return { shouldBuildRoute: false, shouldIndexRoute: false, reason: 'non-storefront brands do not publish product routes' };
  }

  if (isPreviewBrand(id)) {
    return { shouldBuildRoute: true, shouldIndexRoute: false, reason: 'provisional brand preview protection' };
  }

  return { shouldBuildRoute: true, shouldIndexRoute: true, reason: 'eligible route for owned brand' };
}

export function routeFamilyForPath(route: string): SeoRouteFamily {
  if (route.startsWith('/product/')) return 'product';
  if (route.startsWith('/destinations/')) return 'destination';
  if (route.startsWith('/airports/')) return 'airport-guide';
  if (route.startsWith('/guides/')) return 'travel-guide';
  if (/^\/(checkout|success|failed|cancel|pay|orders|top-up)(\/|$)/.test(route)) return 'transactional';
  if (/^\/(blog|comparisons|authors)(\/|$)/.test(route)) return 'editorial';
  return 'utility';
}
